/** Service Desk console: Settings -> Connectors (SD-SC-17), part A: list, connector detail, environments, defaults, call log, health. */
export const serviceConnectors = {
  title: "Connectors",
  subtitle:
    "Every external system Sagot talks to through its API. Each connector has environments, sealed credentials and a library of named operations that the assistant, workflows, the catalogue and orders call.",
  safety:
    "Calls go to public HTTPS hosts only, redirects are not followed and every address is re-checked before each attempt. API answers are treated as data, never as instructions. Write operations always need a confirmation before the assistant can run them.",
  directionLead: "Connectors are for Sagot calling your systems. To let your systems call Sagot, create an ",
  directionLink: "API key",
  directionTail: ".",
  navLocked: "(not in your plan)",
  actions: "Actions",
  upgrade: {
    title: "Connectors are included from the {{plan}} plan",
    body: "Connect your order system, CRM, student or booking records through their API, and let the assistant and workflows look things up. Upgrade to switch Connectors on.",
    cta: "See plans",
  },
  error: { title: "Connectors couldn't load", body: "Check your connection and try again. Nothing was changed." },
  missing: { title: "This connector isn't here", body: "It may have been deleted. Go back to the list of connectors." },
  errors: {
    notInPlan: "Your plan doesn't include this part of Connectors.",
    planLimit: "Your plan's allowance for Connectors is used up. Upgrade to add more.",
    url: {
      empty: "Enter the base URL.",
      invalid: "This is not a valid web address.",
      https: "The address must start with https://.",
      port: "Only the standard HTTPS port (443) is allowed.",
      credentials: "Remove the user name and password from the address. Credentials go in the sealed secrets.",
      private: "Private, local and internal addresses are blocked. Sagot only calls public HTTPS hosts.",
    },
  },
  totals: { label: "Connector totals", connectors: "Connectors", operations: "Operations", active: "Active", attention: "Need attention" },
  filters: { search: "Search connectors", searchPlaceholder: "Name, host or description", status: "Status", allStatuses: "All statuses" },
  empty: {
    title: "No connectors yet",
    body: "Connect a system with an API: start from a template for your kind of system, import an OpenAPI file, a Postman collection or a curl command, or start empty.",
  },
  noMatch: { title: "No connector matches", body: "Try another search or clear the status filter." },
  managed: {
    title: "Managed integrations",
    name: "Zoho CRM and Google",
    note: "Managed OAuth integrations: Sagot keeps the tokens and refreshes them. They are set up in Settings > Integrations, not here.",
    open: "Open Integrations",
  },
  card: {
    noDescription: "No description yet",
    dryRun: "Dry run",
    host: "Host",
    operations: "Operations",
    calls: "Calls, last 24 h",
    callsValue_one: "{{count}} call · error rate {{rate}}",
    callsValue_other: "{{count}} calls · error rate {{rate}}",
    lastCall: "Last call",
    usedBy: "Used by",
    usedByValue_one: "{{count}} action, workflow or sync job",
    usedByValue_other: "{{count}} actions, workflows or sync jobs",
    notUsed: "Not used yet",
    open: "Open",
    openAria: "Open {{name}}",
    operationsAria: "Operations of {{name}}",
    logsAria: "Call logs of {{name}}",
  },
  status: { active: "Active", draft: "Draft", paused: "Paused", error: "Error" },
  health: {
    healthy: "Healthy",
    degraded: "Degraded",
    unhealthy: "Unhealthy",
    none: "No calls yet",
    indicators: "Health indicators",
    status: "Status",
    window_one: "Last {{count}} call",
    window_other: "Last {{count}} calls",
    errorRate: "Error rate",
    alertAt: "Alert above 20 %",
    p95: "p95 latency",
    p50: "p50 {{ms}} ms",
    calls: "Calls, last {{hours}} h",
    callsShort: "Calls",
    failed: "Failed",
    quota: "{{used}} of {{limit}} calls this month",
    quotaUnlimited: "{{used}} calls this month, no limit",
    quotaExceeded: "The plan's monthly allowance of calls is used up. Client flows fall back to \"we'll get back to you\" until it renews.",
    alerts: "Alerts",
    noAlerts: "No open alerts.",
    alertsAbout: "Owners and admins who manage connectors are e-mailed when the error rate passes 20 %, authentication keeps failing or a secret is about to expire (at most once every 6 hours).",
    secretExpiring: "The secret \"{{slot}}\" expires on {{date}}. Rotate it before then.",
    unsafeKinds_one: "{{count}} published operation has a method and a kind that disagree (for example a DELETE set to read), so it skips the confirmation a change needs. Open it, fix the kind and publish again.",
    unsafeKinds_other: "{{count}} published operations have a method and a kind that disagree (for example a DELETE set to read), so they skip the confirmation a change needs. Open each, fix the kind and publish again.",
    perOperation: "By operation",
    noCalls: "No calls in this period yet.",
    controls: "Breaker, capture and retention",
    breakers: "Circuit breaker",
    breaker: { closed: "Closed", open: "Open (paused)", half_open: "Half-open (testing)" },
    capture: "Debug capture for 24 hours",
    captureHint: "Keeps redacted request and answer shapes for troubleshooting. Switches itself off after 24 hours.",
    captureUntil_one: "On until {{date}} · {{count}} exchange captured",
    captureUntil_other: "On until {{date}} · {{count}} exchanges captured",
    captureOn: "Debug capture is on for 24 hours",
    captureOff: "Debug capture is off",
    retention: "Keep the call log for",
    retentionHint: "From 7 to 90 days. Bodies, header values and personal fields are never stored.",
    retentionSaved: "Call log retention updated",
    days_one: "{{count}} day",
    days_other: "{{count}} days",
    clearCache: "Clear cached answers",
    cacheCleared_one: "{{count}} cached answer removed",
    cacheCleared_other: "{{count}} cached answers removed",
  },
  environment: { production: "Production", sandbox: "Sandbox", other: "{{name}}" },
  kind: { generic_rest: "Generic REST" },
  kindOf: { read: "Read", write: "Write", destructive: "Destructive" },
  callers: { staff: "Staff", workflows: "Workflows", assistant: "Assistant", contacts: "Contacts", guests: "Guests" },
  tabs: {
    label: "Connector sections",
    overview: "Overview",
    operations: "Operations library",
    webhooks: "Webhooks in",
    sync: "Sync jobs",
    logs: "Call logs",
    health: "Health",
  },
  header: {
    status: "Connector status",
    pause: "Pause",
    resume: "Resume",
    activate: "Activate",
    paused: "{{name}} paused. Calls fail fast until it is resumed.",
    activated: "{{name}} is active",
    export: "Export",
    exported: "Export saved (no secrets inside)",
  },
  new: {
    button: "New connector",
    title: "New connector",
    subtitle:
      "Connect any system with an HTTPS API: an order or purchasing system, CRM, student, HR or clinic records, bookings, billing, a ticket tool or your own internal API.",
    intro: "Choose how to start. Everything you create stays editable.",
    startFrom: "How do you want to start?",
    generic: "Generic REST",
    genericHint: "Start empty and add operations by hand.",
    templates: "Starter templates by kind of system",
    templatesHint: "Example operations to edit, not code: set your base URL and adjust the paths to your system.",
    moveHere: "Move a connector between workspaces",
    importFile: "Import an export file",
    importFileHint: "A connector exported from Sagot (no secrets inside).",
    fileInvalid: "This is not a connector export file.",
    back: "Back",
    name: "Connector name",
    nameRequired: "Give the connector a name.",
    description: "Description (optional)",
    environment: "Environment",
    environmentHint: "Start with the sandbox if the system has one.",
    baseUrl: "Base URL",
    baseUrlHint: "HTTPS, port 443, a public address. Checked on every call.",
    exampleOperations: "Example operations",
    create: "Create connector",
    created: "{{name}} created",
    someFailed_one: "{{count}} example operation could not be added. Check the operations list.",
    someFailed_other: "{{count}} example operations could not be added. Check the operations list.",
  },
  environments: {
    title: "Environments",
    environment: "Environment",
    state: "State",
    active: "Active",
    inactive: "Not active",
    activeLabel: "Active environment",
    noneActive: "No active environment",
    makeActive: "Make active",
    add: "Add environment",
    edit: "Edit {{name}}",
    delete: "Delete {{name}}",
    key: "Environment key",
    keyHint: "Lowercase letters, numbers and underscores.",
    keyInvalid: "Use lowercase letters, numbers and underscores, starting with a letter.",
    keyTaken: "This connector already has an environment with that key.",
    none: "No environment yet. Add one with a base URL to be able to call this system.",
    dialogHint: "The base URL and how Sagot authenticates there. Secret values are set separately and are never shown again.",
    activateNow: "Make this the active environment",
    saved: "Environment saved",
    deleted: "{{name}} removed",
    deleteTitle: "Remove the {{name}} environment?",
    deleteBody: "Operations stop being able to call this address. The active environment can't be removed.",
    deleteConfirm: "Remove environment",
    switchTitle: "Switch to {{name}}?",
    switchBody:
      "Every operation of this connector, including the ones the assistant and workflows use, will call this environment from now on. The change is audited.",
    switchConfirm: "Switch environment",
    switched: "Active environment: {{name}} · audit logged",
    audited: "Switching the active environment is audited and applies to every operation of this connector.",
  },
  defaults: {
    title: "Request defaults",
    edit: "Edit defaults",
    saved: "Defaults saved",
    headers: "Default headers",
    headersHint: "Sent with every call, up to {{max}}. Credentials go in the authentication, not here.",
    noHeaders: "None",
    timeout: "Timeout (ms)",
    timeoutHint: "1,000 to 30,000. An operation can set its own.",
    timeoutValue: "{{ms}} ms",
    rate: "Rate limit (requests per second)",
    rateHint: "A token bucket per connector, up to {{max}}.",
    rateValue_one: "{{count}} request per second",
    rateValue_other: "{{count}} requests per second",
    rateInvalid: "Use a whole number from 1 to {{max}}.",
    attempts: "Attempts per call",
    attemptsHint: "Only safe methods are retried; writes are never retried blindly.",
    attemptsValue_one: "{{count}} attempt",
    attemptsValue_other: "{{count}} attempts",
    attemptsInvalid: "Use a whole number from 1 to {{max}}.",
    redirects: "Redirects",
    redirectsHint: "Following a redirect is off unless you allow same-host hops.",
    redirectsNone: "Not followed",
    redirectsSameHost: "Same host only (up to 3 hops)",
    breaker: "Circuit breaker",
    breakerValue: "50 % failures over 20 calls pauses the connector for 5 minutes",
    limit: "Answer size",
    limitValue: "1 MB per answer; larger answers fail as too large",
    signature: "Sign requests with X-Sagot-Signature",
    signatureHint: "Also sends a Sagot signature header, signed with the signing_secret slot.",
    fixedIp: "Needs a fixed outbound IP",
    fixedIpHint:
      "Calls to your system then go out through Sagot's fixed outbound addresses, so you can allow them on your firewall. If fixed addresses aren't set up for this workspace, the call is refused with blocked_destination.",
    fixedIpLink: "See Outbound IP addresses in Data & privacy",
    fixedIpReadOn: "Calls go out through Sagot's fixed addresses. They are refused with blocked_destination if those aren't set up.",
    fixedIpReadOff: "Calls may leave from any Sagot address.",
    tokenDomains: "Allowed token domains",
    tokenDomainsHint: "Hosts an OAuth token endpoint may use besides the base URLs, separated by spaces.",
    on: "On",
    off: "Off",
    dryRun: "Dry run for the whole connector",
    dryRunHint: "Every write is rendered and logged but not sent. Use it for pilots and shadow periods.",
    dryRunOn: "Dry run is on: writes are logged, not sent",
    dryRunOff: "Dry run is off: writes are sent",
  },
  safetyCard: {
    title: "Safety",
    guard:
      "Public HTTPS hosts only, redirects off. Private, link-local and metadata addresses are refused, and DNS is re-resolved before every attempt.",
    reconcile: "Re-enter these secrets (they could not be carried over):",
    allowlist: "Host allow-list",
    usedBy: "{{actions}} actions · {{workflows}} workflows · {{profiles}} profiles · {{jobs}} sync jobs",
    logs: "Logs keep method, operation, status and time. Bodies, header values and personal fields are never stored.",
    delete: "Delete connector",
    deleteTitle: "Delete {{name}}?",
    deleteBody: "The connector, its operations and its secrets are removed. A connector that actions or workflows still use can't be deleted: unlink them first.",
    deleteConfirm: "Delete connector",
  },
  operations: {
    intro: "Named operations with a description for the assistant, a parameter schema and saved examples. The assistant and workflows call them by name, for example get_order_status.",
    tableLabel: "Operations of {{name}}",
    operation: "Operation",
    path: "Path",
    kind: "Kind",
    status: "Version",
    who: "Who may invoke",
    filter: "Filter operations",
    all: "All",
    read: "Read",
    write: "Write",
    new: "New operation",
    import: "Import operations",
    edit: "Edit",
    view: "View",
    editAria: "Edit {{key}}",
    deleteAria: "Delete {{key}}",
    draftOnly: "Draft, not published",
    published: "Published v{{version}}",
    hasDraft: "Draft changes",
    notPublished: "Publish it to offer it",
    emptyTitle: "No operations yet",
    emptyBody: "Import an OpenAPI or Postman file, paste a curl command, or add an operation by hand.",
    noMatchTitle: "No operations in this filter",
    noMatchBody: "Change the filter or add an operation.",
    deleteTitle: "Delete {{key}}?",
    deleteBody: "Its versions and examples are removed. An operation that agent actions use can't be deleted: unlink them first.",
    deleteConfirm: "Delete operation",
    deleted: "{{name}} deleted",
  },
  calls: {
    intro: "Every call is logged with the operation, environment, caller, status and time, and kept {{days}} days. Bodies, header values and personal fields are never stored; idempotency keys are shortened.",
    export: "Export CSV",
    exported: "Call log exported",
    exportFailed: "The call log couldn't be exported. Try again.",
    operation: "Operation",
    allOperations: "All operations",
    status: "Status",
    allStatuses: "All statuses",
    environment: "Environment",
    allEnvironments: "All environments",
    outcome: { success: "Succeeded", failed: "Failed", refused: "Refused (not sent)" },
    time: "Time",
    caller: "Caller",
    ref: { test: "Test console", manual_run: "Manual run", workflow_run: "Workflow", action_run: "Assistant action", sync_run: "Sync job", order: "Order", ticket: "Ticket" },
    latency: "Latency",
    attempts: "Attempts",
    key: "Idempotency key",
    error: "Error",
    tableLabel: "Call log",
    emptyTitle: "No calls yet",
    emptyBody: "Calls appear here as soon as an operation runs.",
    emptyFiltered: "No calls match these filters.",
    range: "{{first}} to {{last}} of {{total}}",
    previous: "Previous",
    next: "Next",
  },
  rows: {
    add: "Add",
    nameOf: "{{legend}}, name {{index}}",
    valueOf: "{{legend}}, value {{index}}",
    remove: "Remove {{legend}} {{index}}",
  },
};
